import { call, put, select, takeLatest } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { checkIfValuesFilled, filterOutObjectKeys } from '../../../utilities/object.util'
import { getPcrIdSelector } from '../../home/selectors'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { getPatientSearchState } from '../selectors'
import { PatientSearchService } from '../services/patientSearch.service'
import {
  setAreResultsLoading,
  setPatientSearchResults,
  setShowImportDataConfirmationAlert,
  setShowSharedServiceSearchResultsMessage,
  setTotalAvailableResults,
  setTotalSearchResults,
} from '../store/patientSearch.actions'
import { PatientSearchConstants } from '../store/patientSearch.constants'
import {
  IPatientSearchRequest,
  IPatientSearchResponse,
  IPatientSearchState,
  PatientSearchFilters,
} from '../store/patientSearch.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import Logger from '../../../services/logger.service'

export function* getPatientSearchResults() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const patientSearchState: IPatientSearchState = yield select(getPatientSearchState)
  const { firstName, lastName, sSN, dOB } = patientSearchState
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  try {
    const patientSearchService = new PatientSearchService(authenticationJwt as string)
    const PatientSearchFilters: PatientSearchFilters = [
      {
        id: 'lastName',
        value: lastName,
      },
      {
        id: 'firstName',
        value: firstName,
      },
      {
        id: 'ssn',
        value: sSN,
      },
      {
        id: 'dob',
        value: dOB,
      },
    ]

    const patientSearchRequest: IPatientSearchRequest = {
      filters: PatientSearchFilters,
    }
    yield put(setAreResultsLoading(true))
    const response: IPatientSearchResponse = yield call(
      [patientSearchService, patientSearchService.performSearch],
      patientSearchRequest
    )
    if (response) {
      if (response.status === 200 && response.data) {
        const { searchResults, totalAvailableResults, totalSearchResults } = response.data
        const pcrDocsService = new PcrDocsService(serviceId)
        const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
        if (hasExistingPatientData(pcrDoc)) {
          yield put(setShowImportDataConfirmationAlert(true))
        } else {
          yield put(setShowImportDataConfirmationAlert(false))
        }
        yield put(setPatientSearchResults(searchResults))
        yield put(setTotalAvailableResults(totalAvailableResults))
        yield put(setTotalSearchResults(totalSearchResults))
        if (searchResults && searchResults.length) {
          const hasSharedServiceSearchRecord = searchResults.some(
            (searchResult) => +searchResult.serviceId !== +serviceId
          )
          yield put(setShowSharedServiceSearchResultsMessage(hasSharedServiceSearchRecord))
        }
        Logger.info(
          `PatientSearch: getPatientSearchResults success, results count: ${
            searchResults?.length ?? 0
          }, totalAvailable: ${totalAvailableResults ?? 0}, totalSearch: ${totalSearchResults ?? 0}`
        )
      } else {
        response.failure = true
        if (response.messages?.length) {
          const index = response.messages.findIndex((x) => x.status === response.status)
          response.responseMessage = response.messages[index]?.message
        } else {
          response.responseMessage =
            'There was an error processing your request. Please contact customer support for further assistance.'
        }
        yield put(handleApiError(response))
      }
      yield put(setAreResultsLoading(false))
    }
  } catch (e: any) {
    yield put(setAreResultsLoading(false))
    const cause = e.message ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during patient search. Cause: ${cause}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

function checkPCRPatientObj(pcrDoc: IPcr): boolean {
  if (
    pcrDoc.patient &&
    pcrDoc.patient.patientDemographics &&
    (pcrDoc.patient.patientDemographics.firstName ||
      pcrDoc.patient.patientDemographics.firstNamePNNV ||
      pcrDoc.patient.patientDemographics.lastName ||
      pcrDoc.patient.patientDemographics.lastNamePNNV)
  )
    return true
  else return false
}

function hasExistingPatientData(pcrDoc: IPcr) {
  if (checkPCRPatientObj(pcrDoc)) {
    const fieldsToFilter = [
      'weightUnits',
      'heightUnits',
      'immunizationStatus',
      'immunizationIncompleteReason',
      'immunizationStatusDate',
    ]
    const immunizationFieldsToFilter = ['key', 'year']
    if (
      pcrDoc.patient.patientDemographics &&
      checkIfValuesFilled(filterOutObjectKeys(pcrDoc.patient?.patientDemographics, fieldsToFilter))
    ) {
      return true
    } else if (
      pcrDoc.patient.patientAllergies &&
      pcrDoc.patient.patientAllergies.allergies &&
      pcrDoc.patient.patientAllergies.allergies.length
    ) {
      return true
    } else if (
      pcrDoc.patient.patientMedications &&
      pcrDoc.patient.patientMedications.patientMedicationsList &&
      pcrDoc.patient.patientMedications.patientMedicationsList.length
    ) {
      return true
    } else if (
      pcrDoc.patient.patientMedicalHistory &&
      pcrDoc.patient.patientMedicalHistory.medicalHistory &&
      pcrDoc.patient.patientMedicalHistory.medicalHistory.length
    ) {
      return true
    } else if (
      pcrDoc?.patient?.patientImmunizations &&
      (pcrDoc?.patient?.patientImmunizations?.immunizationStatus?.id ||
        pcrDoc.patient.patientImmunizations.immunizations.some((immunizationRecord) =>
          checkIfValuesFilled(filterOutObjectKeys(immunizationRecord, immunizationFieldsToFilter))
        ))
    ) {
      return true
    }
  }
}

export function* getPatientSearchResultsSaga() {
  yield takeLatest([PatientSearchConstants.GET_PATIENT_SEARCH_RESULTS], performWithMutexHOG, getPatientSearchResults)
}
